import { c as _c } from "react/compiler-runtime";
import { Chip } from '@/comps/chip';
import { TooltipButton } from '@/comps/tooltip-button';
import { cn } from '@/utils/shad';
import { translate, translations } from '@/utils/translations';
import { emotionsHex, EmotionType, SentimentChipByLabel } from '../../user-intelligence/_components';
import { ConversationErrorStatusBadge } from '@/domains/live-activity/interactions/components/conversationsUtils';
import { Check, ChevronDown, CircleX, Loader2, Plus, Star } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { Button } from '@/comps/primitives/button';
import { ClassificationMode, GetInteractionAggregatesReqGroupBy, SentimentFieldFieldValue, TextLanguageFieldFieldValue, UpdateFieldReqField, ValidEmotion } from '@/api/generated/model';
import { humanizeString } from '@/utils/string.utils';
import { SearchInputV2 } from '@/comps/search-input';
import { useProjectsContext } from '@/providers/projects-provider';
import { toast } from 'sonner';
import { QUERY_KEY_INTERACTION_DETAILS } from '@/constants/query-keys';
import { useQueryClient } from '@tanstack/react-query';
import { WithClassName } from '@/types/component';
import { useAuthContext } from '@/hooks/use-auth';
import { useFetchInteractionAggregates } from '@/models/interaction-aggregates/useFetchInteractionAggregates';
import { buildUpdateFieldPayload } from './home-page-interactions-sidebar-item.utils';
import { getDateTRangeFromKindWithoutCustom } from '@/utils/get-date-range-from-kind';
import { WithLoadingContainer } from '@/comps/loading-spinner';
import { useUpdateField } from '@/api/generated/interactions/interactions';
type FieldValueMap = {
  topic?: string;
  intent?: string;
  user_action?: string;
  category?: string;
  risky_behavior?: string;
  model_suggestion?: string;
  custom_aggregate?: string;
  text_language?: TextLanguageFieldFieldValue;
  sentiment?: SentimentFieldFieldValue;
  emotions?: ValidEmotion;
};
interface EditableFieldProps<T extends keyof FieldValueMap> {
  fieldName: T;
  value?: FieldValueMap[T];
  interactionId: string;
  placeholder: string;
  translateFn?: (value?: FieldValueMap[T] | string) => string;
  clearable?: boolean;
  editable?: boolean;
  displayFn?: (value: string, clearable: boolean) => React.ReactNode;
  triggerDisplayFn?: (value: string, clearable: boolean) => React.ReactNode;
  /**
   * For custom aggregate axes only: the internal name of the custom axis
   * (`ProjectAggregate.category_name`) whose class is being edited.
   */
  customAggregateName?: string;
}
export function EditableField(t0) {
  const $ = _c(52);
  const {
    value,
    interactionId,
    fieldName,
    placeholder,
    translateFn,
    clearable: t1,
    editable: t2,
    displayFn,
    triggerDisplayFn,
    customAggregateName
  } = t0;
  const clearable = t1 === undefined ? false : t1;
  const editable = t2 === undefined ? true : t2;
  const {
    isCompanyAdmin
  } = useAuthContext();
  const {
    projectId
  } = useProjectsContext();
  const queryClient = useQueryClient();
  const isThumbsDownIssue = fieldName === "model_suggestion" && value === "thumbs down";
  const disabled = !isCompanyAdmin || isThumbsDownIssue;
  let t3;
  bb0: {
    if (fieldName === "model_suggestion" && !value) {
      t3 = true;
      break bb0;
    }
    if (fieldName === "custom_aggregate" && !value) {
      t3 = editable;
      break bb0;
    }
    t3 = false;
  }
  const canAddMore = t3;
  let t4;
  if ($[0] !== queryClient) {
    t4 = {
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_INTERACTION_DETAILS]
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    mutateAsync: updateField,
    isPending: isUpdatingField
  } = useUpdateField(t4);
  let t5;
  if ($[2] !== customAggregateName) {
    t5 = (fieldName_0, newValue, oldValue) => buildUpdateFieldPayload(fieldName_0, newValue, oldValue, customAggregateName);
    $[2] = customAggregateName;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const buildFieldPayload = t5;
  let t6;
  if ($[4] !== buildFieldPayload || $[5] !== fieldName || $[6] !== interactionId || $[7] !== projectId || $[8] !== updateField || $[9] !== value) {
    t6 = async newValue_0 => {
      if (!interactionId) {
        toast.error("Missing interaction ID");
        return;
      }
      const field = buildFieldPayload(fieldName, newValue_0, value);
      toast.promise(updateField({
        data: {
          project_id: projectId,
          interaction_id: interactionId,
          field
        }
      }), {
        loading: "Updating field...",
        success: "Field updated successfully",
        error: "Failed to update field"
      });
    };
    $[4] = buildFieldPayload;
    $[5] = fieldName;
    $[6] = interactionId;
    $[7] = projectId;
    $[8] = updateField;
    $[9] = value;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleSelect = t6;
  let t7;
  if ($[11] !== disabled || $[12] !== fieldName || $[13] !== handleSelect || $[14] !== value) {
    t7 = (val, clearable_0) => {
      if (!val) {
        return null;
      }
      switch (fieldName) {
        case "sentiment":
          {
            return <SentimentChipByLabel clickable={!disabled} sentimentLabel={translate("sentiment", val as string)} />;
          }
        case "emotions":
          {
            return <EmotionChip removable={clearable_0 && !!value} emotion={val as ValidEmotion} onRemove={() => {
              handleSelect(null);
            }} />;
          }
        default:
          {
            return <span>{humanizeString(String(val))}</span>;
          }
      }
    };
    $[11] = disabled;
    $[12] = fieldName;
    $[13] = handleSelect;
    $[14] = value;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const defaultDisplayFn = t7;
  const compoundTranslateFn = translateFn ?? humanizeString;
  const compoundDisplayFn = displayFn ?? defaultDisplayFn;
  const compoundTriggerDisplayFn = triggerDisplayFn ?? compoundDisplayFn;
  if (!value && !canAddMore && fieldName !== "emotions") {
    return "NA";
  }
  const displayValueForTrigger = fieldName === "emotions" ? value as ValidEmotion | undefined ?? "neutral" : value;
  let t8;
  if ($[16] !== clearable || $[17] !== compoundDisplayFn || $[18] !== compoundTranslateFn || $[19] !== compoundTriggerDisplayFn || $[20] !== customAggregateName || $[21] !== disabled || $[22] !== displayValueForTrigger || $[23] !== editable || $[24] !== fieldName || $[25] !== handleSelect || $[26] !== isUpdatingField || $[27] !== placeholder || $[28] !== value) {
    t8 = (fieldName === "emotions" || value) && <EditFieldPopover placeholder={placeholder} fieldName={fieldName} handleSelect={handleSelect} value={value} displayFn={compoundDisplayFn} translateFn={compoundTranslateFn} customAggregateName={customAggregateName}><button disabled={disabled || isUpdatingField || !editable} className={cn("flex h-auto items-center gap-1 p-0 px-1 font-normal", isUpdatingField && "cursor-not-allowed opacity-50")}>{compoundTriggerDisplayFn(displayValueForTrigger as string, clearable)}<ChevronDown className={disabled || !editable ? "hidden" : ""} size={12} /></button></EditFieldPopover>;
    $[16] = clearable;
    $[17] = compoundDisplayFn;
    $[18] = compoundTranslateFn;
    $[19] = compoundTriggerDisplayFn;
    $[20] = customAggregateName;
    $[21] = disabled;
    $[22] = displayValueForTrigger;
    $[23] = editable;
    $[24] = fieldName;
    $[25] = handleSelect;
    $[26] = isUpdatingField;
    $[27] = placeholder;
    $[28] = value;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== canAddMore || $[31] !== compoundDisplayFn || $[32] !== compoundTranslateFn || $[33] !== customAggregateName || $[34] !== fieldName || $[35] !== handleSelect || $[36] !== isUpdatingField || $[37] !== placeholder || $[38] !== value) {
    t9 = canAddMore && <EditFieldPopover placeholder={placeholder} fieldName={fieldName} handleSelect={handleSelect} value={value} displayFn={compoundDisplayFn} translateFn={compoundTranslateFn} customAggregateName={customAggregateName}><Button variant="outline" className="h-auto rounded-lg border-dashed px-2 py-0 text-xs font-medium text-foreground opacity-90" PrependIcon={isUpdatingField ? Loader2 : Plus} iconClassName={cn(isUpdatingField && "animate-spin")}>Add {placeholder.toLowerCase()}</Button></EditFieldPopover>;
    $[30] = canAddMore;
    $[31] = compoundDisplayFn;
    $[32] = compoundTranslateFn;
    $[33] = customAggregateName;
    $[34] = fieldName;
    $[35] = handleSelect;
    $[36] = isUpdatingField;
    $[37] = placeholder;
    $[38] = value;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== canAddMore || $[41] !== clearable || $[42] !== disabled || $[43] !== fieldName || $[44] !== handleSelect || $[45] !== isCompanyAdmin || $[46] !== isUpdatingField) {
    t10 = clearable && !canAddMore && fieldName !== "emotions" && isCompanyAdmin && <button disabled={isUpdatingField || disabled} onClick={async e => {
      e.preventDefault();
      e.stopPropagation();
      handleSelect(null);
    }}><CircleX size={14} className={cn("ml-1 text-xs text-foreground transition-opacity hover:opacity-70", (isUpdatingField || disabled) && "cursor-not-allowed opacity-50 hover:opacity-50")} /></button>;
    $[40] = canAddMore;
    $[41] = clearable;
    $[42] = disabled;
    $[43] = fieldName;
    $[44] = handleSelect;
    $[45] = isCompanyAdmin;
    $[46] = isUpdatingField;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== t10 || $[49] !== t8 || $[50] !== t9) {
    t11 = <div className="flex items-center gap-0">{t8}{t9}{t10}</div>;
    $[48] = t10;
    $[49] = t8;
    $[50] = t9;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  return t11;
}
function EditFieldPopover(props) {
  const $ = _c(52);
  const {
    placeholder,
    fieldName,
    handleSelect,
    value,
    displayFn,
    translateFn,
    children,
    customAggregateName
  } = props;
  const [open, setOpen] = useState(false);
  const {
    projectId
  } = useProjectsContext();
  const [search, setSearch] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getDateTRangeFromKindWithoutCustom("12_months");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const timeRange = t0;
  const isCustomAggregateField = fieldName === "custom_aggregate";
  let t1;
  bb0: {
    if (isCustomAggregateField) {
      t1 = open && !!customAggregateName;
      break bb0;
    }
    let t2;
    if ($[1] !== fieldName || $[2] !== open) {
      t2 = ["topic", "user_intent", "user_action", "category", "risky_behavior", "model_suggestion"].includes(fieldName) && open;
      $[1] = fieldName;
      $[2] = open;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  const queryEnabled = t1;
  let t2;
  bb1: {
    if (isCustomAggregateField) {
      const t3 = customAggregateName ?? "";
      let t4;
      if ($[4] !== t3) {
        t4 = {
          kind: "custom_aggregate",
          custom_aggregate: t3
        };
        $[4] = t3;
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      t2 = t4;
      break bb1;
    }
    const t3 = fieldName === "topic" ? "topic" : fieldName === "intent" ? "user_intent" : fieldName === "user_action" ? "user_action" : fieldName === "category" ? "category" : fieldName === "risky_behavior" ? "negative_user_intent_v2" : "type_of_problem_v2";
    let t4;
    if ($[6] !== t3) {
      t4 = {
        kind: t3
      };
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t2 = t4;
  }
  const groupBy = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== groupBy || $[10] !== projectId) {
    t4 = {
      group_by: groupBy,
      filters: t3,
      time_range: timeRange,
      project_id: projectId,
      limit: 1000
    };
    $[9] = groupBy;
    $[10] = projectId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== queryEnabled || $[13] !== t4) {
    t5 = {
      aggregateBy: "interaction",
      payload: t4,
      enabled: queryEnabled
    };
    $[12] = queryEnabled;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const dataQuery = useFetchInteractionAggregates(t5);
  let t6;
  if ($[15] !== dataQuery.data?.aggregates || $[16] !== fieldName) {
    bb2: {
      if (fieldName === "text_language") {
        let t7;
        if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = Object.values(TextLanguageFieldFieldValue);
          $[18] = t7;
        } else {
          t7 = $[18];
        }
        t6 = t7;
        break bb2;
      }
      if (fieldName === "sentiment") {
        let t7;
        if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = Object.values(SentimentFieldFieldValue);
          $[19] = t7;
        } else {
          t7 = $[19];
        }
        t6 = t7;
        break bb2;
      }
      if (fieldName === "emotions") {
        let t7;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = Object.values(ValidEmotion);
          $[20] = t7;
        } else {
          t7 = $[20];
        }
        t6 = t7;
        break bb2;
      }
      const values = dataQuery.data?.aggregates.map(_temp) ?? [];
      if (fieldName === "model_suggestion") {
        t6 = values.filter(_temp2);
        break bb2;
      }
      t6 = values;
    }
    $[15] = dataQuery.data?.aggregates;
    $[16] = fieldName;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const availableOptions = t6;
  let t7;
  if ($[21] !== availableOptions || $[22] !== search || $[23] !== translateFn) {
    let t8;
    if ($[25] !== search || $[26] !== translateFn) {
      t8 = opt => translateFn(opt).toLowerCase().includes(search.toLowerCase());
      $[25] = search;
      $[26] = translateFn;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    let t9;
    if ($[28] !== translateFn) {
      t9 = (a, b) => translateFn(a).localeCompare(translateFn(b));
      $[28] = translateFn;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    t7 = availableOptions.filter(t8).toSorted(t9);
    $[21] = availableOptions;
    $[22] = search;
    $[23] = translateFn;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const filteredOptions = t7;
  let t8;
  if ($[30] !== children) {
    t8 = <PopoverTrigger asChild={true}>{children}</PopoverTrigger>;
    $[30] = children;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = document.querySelector("#warning-sheet");
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const t10 = `Search ${placeholder}`;
  let t11;
  if ($[33] !== search || $[34] !== t10) {
    t11 = <div className="border-b p-2"><SearchInputV2 search={search} setSearch={setSearch} placeholder={t10} /></div>;
    $[33] = search;
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== displayFn || $[37] !== filteredOptions || $[38] !== handleSelect || $[39] !== placeholder || $[40] !== value) {
    t12 = filteredOptions.length === 0 ? <div className="p-2 text-sm text-muted-foreground">No {placeholder.toLowerCase()} found</div> : filteredOptions.map(opt_0 => <div key={opt_0} role="button" tabIndex={0} onKeyDown={e => {
      if (e.key === "Enter") {
        handleSelect(opt_0);
        setOpen(false);
      }
    }} onClick={() => {
      handleSelect(opt_0);
      setOpen(false);
    }} className={`flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-accent ${opt_0 === value ? "bg-accent font-medium" : ""}`}><span>{displayFn(opt_0, false)}</span>{opt_0 === value && <Check size={12} />}</div>);
    $[36] = displayFn;
    $[37] = filteredOptions;
    $[38] = handleSelect;
    $[39] = placeholder;
    $[40] = value;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== dataQuery.isFetching || $[43] !== t12) {
    t13 = <WithLoadingContainer hideChildren={true} loading={dataQuery.isFetching} className="bg-alter max-h-75 min-h-10 overflow-y-auto">{t12}</WithLoadingContainer>;
    $[42] = dataQuery.isFetching;
    $[43] = t12;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t11 || $[46] !== t13) {
    t14 = <PopoverContent container={t9} forceMount={true} align="start" side="bottom" className="min-w-50 bg-background">{t11}{t13}</PopoverContent>;
    $[45] = t11;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== open || $[49] !== t14 || $[50] !== t8) {
    t15 = <Popover open={open} onOpenChange={setOpen}>{t8}{t14}</Popover>;
    $[48] = open;
    $[49] = t14;
    $[50] = t8;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  return t15;
}
function _temp2(val) {
  return val !== "thumbs down";
}
function _temp(agg) {
  return agg.groupName;
}
interface IBaseProps {
  title: string;
  translationKey?: string;
}
interface ITopicProps extends IBaseProps {
  type: 'topic';
  value?: string;
  interactionId: string;
}
interface IIntentProps extends IBaseProps {
  type: 'intent';
  value?: string;
  interactionId: string;
}
interface IActionProps extends IBaseProps {
  type: 'user_action';
  value?: string;
  interactionId: string;
}
interface ICategoryProps extends IBaseProps {
  type: 'category';
  value?: string;
  interactionId: string;
}
interface IRiskyBehaviorProps extends IBaseProps {
  type: 'risky_behavior';
  value?: string;
  interactionId: string;
}
interface IModelSuggestionProps extends IBaseProps {
  type: 'model_suggestion';
  value?: string;
  interactionId: string;
}
interface ICustomAggregateProps extends IBaseProps {
  type: 'custom_aggregate';
  values?: string[];
  interactionId: string;
  aggregateName: string;
  classificationMode?: ClassificationMode;
}
interface ISentimentProps extends IBaseProps {
  type: 'sentiment';
  value: string;
  interactionId: string;
}
interface ILanguageProps extends IBaseProps {
  type: 'language';
  value?: string;
  interactionId: string;
}
interface IEmotionsProps extends IBaseProps {
  type: 'emotions';
  value?: string[];
  interactionId: string;
}
interface IRatingProps extends IBaseProps {
  type: 'rating';
  value: string | number;
  interactionId?: never;
}
interface IDropoffSentimentProps extends IBaseProps {
  type: 'dropoff_sentiment';
  value: string | number;
  interactionId?: never;
}
interface IConversationErrorStatusProps extends IBaseProps {
  type: 'conversation_error_status';
  value: string;
  interactionId?: never;
}
interface IDefaultProps extends IBaseProps {
  type?: undefined;
  value: number | string | string[];
  interactionId?: never;
}
export type Props = ITopicProps | IIntentProps | IActionProps | ICategoryProps | IRiskyBehaviorProps | IModelSuggestionProps | ICustomAggregateProps | ISentimentProps | ILanguageProps | IEmotionsProps | IRatingProps | IDropoffSentimentProps | IConversationErrorStatusProps | IDefaultProps;
function SidebarItemRow(t0) {
  const $ = _c(10);
  const {
    title,
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex gap-2 text-[13px] leading-5.5 font-medium tracking-[0.15px] text-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <span className="min-w-max">{title}: </span>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <span className="ml-0.5 min-w-0 text-[12px] leading-[1.6] font-normal tracking-[0.15px] wrap-break-word text-muted-foreground">{children}</span>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export const HomePageInteractionsSidebarItem = props => {
  const $ = _c(86);
  if (props.type === "topic") {
    let t0;
    if ($[0] !== props.interactionId || $[1] !== props.value) {
      t0 = <EditableField fieldName="topic" placeholder="topics" interactionId={props.interactionId} value={props.value} />;
      $[0] = props.interactionId;
      $[1] = props.value;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== props.title || $[4] !== t0) {
      t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
      $[3] = props.title;
      $[4] = t0;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  if (props.type === "intent") {
    let t0;
    if ($[6] !== props.interactionId || $[7] !== props.value) {
      t0 = <EditableField fieldName="intent" placeholder="intents" interactionId={props.interactionId} value={props.value} />;
      $[6] = props.interactionId;
      $[7] = props.value;
      $[8] = t0;
    } else {
      t0 = $[8];
    }
    let t1;
    if ($[9] !== props.title || $[10] !== t0) {
      t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
      $[9] = props.title;
      $[10] = t0;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    return t1;
  }
  if (props.type === "user_action") {
    let t0;
    if ($[12] !== props.interactionId || $[13] !== props.value) {
      t0 = <EditableField fieldName="user_action" placeholder="actions" interactionId={props.interactionId} value={props.value} />;
      $[12] = props.interactionId;
      $[13] = props.value;
      $[14] = t0;
    } else {
      t0 = $[14];
    }
    let t1;
    if ($[15] !== props.title || $[16] !== t0) {
      t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
      $[15] = props.title;
      $[16] = t0;
      $[17] = t1;
    } else {
      t1 = $[17];
    }
    return t1;
  }
  if (props.type === "category") {
    let t0;
    if ($[18] !== props.interactionId || $[19] !== props.value) {
      t0 = <EditableField fieldName="category" placeholder="categories" interactionId={props.interactionId} value={props.value} />;
      $[18] = props.interactionId;
      $[19] = props.value;
      $[20] = t0;
    } else {
      t0 = $[20];
    }
    let t1;
    if ($[21] !== props.title || $[22] !== t0) {
      t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
      $[21] = props.title;
      $[22] = t0;
      $[23] = t1;
    } else {
      t1 = $[23];
    }
    return t1;
  }
  if (props.type === "risky_behavior") {
    let t0;
    if ($[24] !== props.interactionId || $[25] !== props.value) {
      t0 = <EditableField fieldName="risky_behavior" placeholder="business risk" interactionId={props.interactionId} value={props.value} clearable={true} editable={false} />;
      $[24] = props.interactionId;
      $[25] = props.value;
      $[26] = t0;
    } else {
      t0 = $[26];
    }
    let t1;
    if ($[27] !== props.title || $[28] !== t0) {
      t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
      $[27] = props.title;
      $[28] = t0;
      $[29] = t1;
    } else {
      t1 = $[29];
    }
    return t1;
  }
  if (props.type === "model_suggestion") {
    let t0;
    if ($[30] !== props.interactionId || $[31] !== props.value) {
      t0 = <EditableField fieldName="model_suggestion" placeholder="type of problem" interactionId={props.interactionId} value={props.value} clearable={true} />;
      $[30] = props.interactionId;
      $[31] = props.value;
      $[32] = t0;
    } else {
      t0 = $[32];
    }
    let t1;
    if ($[33] !== props.title || $[34] !== t0) {
      t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
      $[33] = props.title;
      $[34] = t0;
      $[35] = t1;
    } else {
      t1 = $[35];
    }
    return t1;
  }
  if (props.type === "custom_aggregate") {
    const currentValue = props.values?.[0];
    const isMultiLabel = props.classificationMode === ClassificationMode.multi_label;
    const t0 = props.title;
    let t1;
    if ($[36] !== props.title) {
      t1 = props.title.toLowerCase();
      $[36] = props.title;
      $[37] = t1;
    } else {
      t1 = $[37];
    }
    let t2;
    if ($[38] !== props.values) {
      t2 = () => props.values?.map(_temp3).join(", ");
      $[38] = props.values;
      $[39] = t2;
    } else {
      t2 = $[39];
    }
    const t3 = !isMultiLabel;
    const t4 = !isMultiLabel;
    let t5;
    if ($[40] !== currentValue || $[41] !== props.aggregateName || $[42] !== props.interactionId || $[43] !== t1 || $[44] !== t2 || $[45] !== t3 || $[46] !== t4) {
      t5 = <EditableField fieldName="custom_aggregate" placeholder={t1} interactionId={props.interactionId} value={currentValue} customAggregateName={props.aggregateName} triggerDisplayFn={t2} editable={t3} clearable={t4} />;
      $[40] = currentValue;
      $[41] = props.aggregateName;
      $[42] = props.interactionId;
      $[43] = t1;
      $[44] = t2;
      $[45] = t3;
      $[46] = t4;
      $[47] = t5;
    } else {
      t5 = $[47];
    }
    let t6;
    if ($[48] !== props.title || $[49] !== t5) {
      t6 = <SidebarItemRow title={t0}>{t5}</SidebarItemRow>;
      $[48] = props.title;
      $[49] = t5;
      $[50] = t6;
    } else {
      t6 = $[50];
    }
    return t6;
  }
  if (props.type === "language") {
    const t0 = props.value as TextLanguageFieldFieldValue;
    let t1;
    if ($[51] !== props.interactionId || $[52] !== t0) {
      t1 = <EditableField fieldName="text_language" placeholder="languages" interactionId={props.interactionId} value={t0} />;
      $[51] = props.interactionId;
      $[52] = t0;
      $[53] = t1;
    } else {
      t1 = $[53];
    }
    let t2;
    if ($[54] !== props.title || $[55] !== t1) {
      t2 = <SidebarItemRow title={props.title}>{t1}</SidebarItemRow>;
      $[54] = props.title;
      $[55] = t1;
      $[56] = t2;
    } else {
      t2 = $[56];
    }
    return t2;
  }
  if (props.type === "sentiment") {
    const t0 = props.value as SentimentFieldFieldValue;
    let t1;
    if ($[57] !== props.interactionId || $[58] !== t0) {
      t1 = <span className="pt-0.5"><EditableField fieldName="sentiment" placeholder="sentiments" interactionId={props.interactionId} value={t0} /></span>;
      $[57] = props.interactionId;
      $[58] = t0;
      $[59] = t1;
    } else {
      t1 = $[59];
    }
    let t2;
    if ($[60] !== props.title || $[61] !== t1) {
      t2 = <SidebarItemRow title={props.title} className="items-center">{t1}</SidebarItemRow>;
      $[60] = props.title;
      $[61] = t1;
      $[62] = t2;
    } else {
      t2 = $[62];
    }
    return t2;
  }
  if (props.type === "emotions") {
    const primary = props.value?.[0];
    const t0 = primary as ValidEmotion | undefined;
    const t1 = !!primary && primary !== "neutral";
    let t2;
    if ($[63] !== props.interactionId || $[64] !== t0 || $[65] !== t1) {
      t2 = <div className="ml-1 inline-flex gap-2"><EditableField fieldName="emotions" placeholder="emotions" interactionId={props.interactionId} value={t0} clearable={t1} /></div>;
      $[63] = props.interactionId;
      $[64] = t0;
      $[65] = t1;
      $[66] = t2;
    } else {
      t2 = $[66];
    }
    let t3;
    if ($[67] !== props.title || $[68] !== t2) {
      t3 = <SidebarItemRow title={props.title} className="items-center">{t2}</SidebarItemRow>;
      $[67] = props.title;
      $[68] = t2;
      $[69] = t3;
    } else {
      t3 = $[69];
    }
    return t3;
  }
  if (props.type === "rating") {
    let t0;
    if ($[70] !== props.value) {
      t0 = <span className="pt-0.5"><StarRating rating={props.value} /></span>;
      $[70] = props.value;
      $[71] = t0;
    } else {
      t0 = $[71];
    }
    let t1;
    if ($[72] !== props.title || $[73] !== t0) {
      t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
      $[72] = props.title;
      $[73] = t0;
      $[74] = t1;
    } else {
      t1 = $[74];
    }
    return t1;
  }
  if (props.type === "conversation_error_status") {
    let t0;
    if ($[75] !== props.value) {
      t0 = <span className="pt-0.5"><ConversationErrorStatusBadge status={props.value} /></span>;
      $[75] = props.value;
      $[76] = t0;
    } else {
      t0 = $[76];
    }
    let t1;
    if ($[77] !== props.title || $[78] !== t0) {
      t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
      $[77] = props.title;
      $[78] = t0;
      $[79] = t1;
    } else {
      t1 = $[79];
    }
    return t1;
  }
  let t0;
  if ($[80] !== props.translationKey || $[81] !== props.value) {
    t0 = typeof props.value === "string" ? <span className="pt-0.5">{props.value}</span> : <span className="ml-0.5 inline-flex gap-0.5">{props.value instanceof Array ? <ul className="flex flex-wrap gap-1">{props.value.map((item, index) => <li key={index}><Chip variant="muted" size="small" className="text-[12px] text-muted-foreground">{props.translationKey ? translate(props.translationKey as keyof typeof translations, item) : item}</Chip></li>)}</ul> : props.value}</span>;
    $[80] = props.translationKey;
    $[81] = props.value;
    $[82] = t0;
  } else {
    t0 = $[82];
  }
  let t1;
  if ($[83] !== props.title || $[84] !== t0) {
    t1 = <SidebarItemRow title={props.title}>{t0}</SidebarItemRow>;
    $[83] = props.title;
    $[84] = t0;
    $[85] = t1;
  } else {
    t1 = $[85];
  }
  return t1;
};
export const StarRating = t0 => {
  const $ = _c(7);
  const {
    rating,
    size
  } = t0;
  const ratingNumber = Number(rating);
  if (!rating || ratingNumber < 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="line-clamp-4 text-[12px] text-muted-foreground">NA</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      display: "flex",
      gap: "4px",
      alignItems: "center"
    };
    t2 = [...Array(5)];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== ratingNumber || $[4] !== size || $[5] !== t2) {
    t3 = <div style={t1}>{t2.map((_, index) => <Star key={index} size={size ?? 16} color={index < ratingNumber ? "#F1B74D" : "#d3d3d3"} fill={index < ratingNumber ? "#F1B74D" : "none"} />)}</div>;
    $[3] = ratingNumber;
    $[4] = size;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const EmotionChip = t0 => {
  const $ = _c(33);
  const {
    emotion,
    onClick,
    removable: t1,
    onRemove
  } = t0;
  const removable = t1 === undefined ? false : t1;
  const emotionOrNone = emotion ?? "none";
  let t2;
  if ($[0] !== emotionOrNone) {
    t2 = translate("emotions", emotionOrNone);
    $[0] = emotionOrNone;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const emotionLabel = t2;
  let t3;
  let t4;
  if ($[2] !== emotion || $[3] !== onClick) {
    t3 = e => {
      if (e.key === "Enter") {
        if (!emotion) {
          return;
        }
        onClick?.(emotion);
      }
    };
    t4 = () => {
      if (!emotion) {
        return;
      }
      onClick?.(emotion);
    };
    $[2] = emotion;
    $[3] = onClick;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  const t5 = removable && "hover:cursor-pointer hover:opacity-80";
  let t6;
  if ($[6] !== t5) {
    t6 = cn("flex cursor-default items-center justify-center gap-1.5 rounded-[20px] px-2 py-0 font-medium underline-offset-4 opacity-100 transition-opacity", t5);
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = emotionsHex[emotionLabel as EmotionType] + "66";
  let t8;
  if ($[8] !== t7) {
    t8 = {
      backgroundColor: t7
    };
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = emotionOrNone.length > 50;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("inline size-1.5 rounded-full bg-n-violet-platform");
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = emotionsHex[emotionLabel as EmotionType];
  let t12;
  if ($[11] !== t11) {
    t12 = <div className={t10} style={{
      backgroundColor: t11
    }} />;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== emotionLabel) {
    t13 = <span className="line-clamp-1 py-0.5 text-xs text-foreground">{emotionLabel}</span>;
    $[13] = emotionLabel;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== emotion || $[16] !== onRemove || $[17] !== removable) {
    t14 = removable && emotion && <CircleX size={14} onClick={e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      onRemove?.(emotion);
    }} className="ml-1 text-xs text-foreground transition-opacity hover:opacity-70" />;
    $[15] = emotion;
    $[16] = onRemove;
    $[17] = removable;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t12 || $[20] !== t13 || $[21] !== t14) {
    t15 = <div className="flex items-center justify-center gap-1">{t12}{t13}{t14}</div>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== emotionOrNone || $[24] !== t15 || $[25] !== t9) {
    t16 = <TooltipButton enabled={t9} content={emotionOrNone}>{t15}</TooltipButton>;
    $[23] = emotionOrNone;
    $[24] = t15;
    $[25] = t9;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t16 || $[28] !== t3 || $[29] !== t4 || $[30] !== t6 || $[31] !== t8) {
    t17 = <div role="button" tabIndex={0} onKeyDown={t3} onClick={t4} className={t6} style={t8}>{t16}</div>;
    $[27] = t16;
    $[28] = t3;
    $[29] = t4;
    $[30] = t6;
    $[31] = t8;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
};
function _temp3(aggregateValue) {
  return humanizeString(aggregateValue);
}